import React, { useEffect, useState } from 'react'
import { useHistory, useParams } from 'react-router-dom'
import { api } from '../../utils'
import SetTitle from '../../utils/set_title'
import { Alert, Button, Card, CardBody, CardHeader, Col, FormGroup, Input, Label, Row, Spinner } from 'reactstrap'
import { ToastContainer } from 'react-toastify'
import 'react-toastify/dist/ReactToastify.css'
import { notify } from '../../utils/notify'
import WModalGeneric from '../../components/molecules/WModalGeneric'

function formatRecoveryError(error) {
  const responseData = error?.response?.data

  if (typeof responseData === 'string' && responseData.trim()) {
    return responseData
  }

  if (responseData?.error) {
    return responseData.error
  }

  if (responseData && typeof responseData === 'object') {
    const firstValue = Object.values(responseData)[0]
    if (Array.isArray(firstValue) && firstValue.length > 0) {
      return firstValue[0]
    }
    if (typeof firstValue === 'string' && firstValue.trim()) {
      return firstValue
    }
  }

  return 'Impossibile pianificare il recupero volumi.'
}

function buildRecoveryOutcomeMessage(data) {
  const summary = []

  if (data?.created_count) {
    summary.push(`${data.created_count} month${data.created_count === 1 ? '' : 's'} scheduled`)
  }
  if (data?.skipped_count) {
    summary.push(`${data.skipped_count} skipped`)
  }
  if (data?.blocked_count) {
    summary.push(`${data.blocked_count} blocked`)
  }

  if (summary.length === 0) {
    return {
      kind: 'warning',
      message: 'No recovery months were scheduled.',
    }
  }

  return {
    kind: data?.skipped_count || data?.blocked_count ? 'warning' : 'success',
    message: `Recovery result: ${summary.join(' | ')}.`,
  }
}

function VolumeRetrieveHistoricalRecovery() {
  SetTitle('Historical Recovery')

  const { id } = useParams()
  const history = useHistory()

  const [volumeRetrieve, setVolumeRetrieve] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [recoveryStartMonth, setRecoveryStartMonth] = useState('')
  const [recoveryEndMonth, setRecoveryEndMonth] = useState('')
  const [recoveryForce, setRecoveryForce] = useState(false)
  const [showRecoveryConfirm, setShowRecoveryConfirm] = useState(false)
  const [recoverySubmitting, setRecoverySubmitting] = useState(false)

  useEffect(() => {
    async function load() {
      try {
        const response = await api.get(`/market-screener-detail/${id}`)
        setVolumeRetrieve(response.data)
      } catch (err) {
        setError('Error fetching market screener details.')
      } finally {
        setLoading(false)
      }
    }

    load()
  }, [id])

  const validateRecoveryRange = () => {
    if (!recoveryStartMonth || !recoveryEndMonth) {
      notify('Select both recovery months before continuing.', 'error')
      return false
    }

    if (recoveryEndMonth < recoveryStartMonth) {
      notify('Recovery end month must be greater than or equal to the start month.', 'error')
      return false
    }

    return true
  }

  const openRecoveryConfirmation = () => {
    if (!validateRecoveryRange()) {
      return
    }

    setShowRecoveryConfirm(true)
  }

  const submitRecoveryRange = async () => {
    if (recoverySubmitting || !validateRecoveryRange()) {
      return
    }

    setRecoverySubmitting(true)

    try {
      const response = await api.post(`volume-report-scheduled/${id}/recover-month/`, {
        reference_month_start: recoveryStartMonth,
        reference_month_end: recoveryEndMonth,
        force: recoveryForce,
      })
      const outcome = buildRecoveryOutcomeMessage(response?.data)
      notify(outcome.message, outcome.kind)
      setShowRecoveryConfirm(false)
    } catch (err) {
      notify(formatRecoveryError(err), 'error')
    } finally {
      setRecoverySubmitting(false)
    }
  }

  if (loading) {
    return <Spinner />
  }

  if (error) {
    return <div className="text-center text-danger">{error}</div>
  }

  return (
    <>
      <WModalGeneric
        show={showRecoveryConfirm}
        onHide={() => setShowRecoveryConfirm(false)}
        onSubmit={submitRecoveryRange}
        title="Confirm volume recovery"
        confirmText={recoverySubmitting ? 'Conferma in corso...' : 'Conferma'}
        body={
          <div style={{ textAlign: 'center' }}>
            <p style={{ marginBottom: '12px' }}>
              Schedule a recovery for every month in the selected range, inclusive?
            </p>
            <p style={{ marginBottom: '12px' }}>
              <strong>{recoveryStartMonth || '-'}</strong> to{' '}
              <strong>{recoveryEndMonth || '-'}</strong>
            </p>
            <p style={{ marginBottom: 0 }}>
              {recoveryForce
                ? 'Existing historical artifacts will be overwritten when possible.'
                : 'Months that already have historical artifacts will be skipped.'}
            </p>
          </div>
        }
      />
      <div className="pb-5">
        <Card className="bg-secondary shadow">
          <CardHeader className="bg-white border-0">
            <Row style={{ marginLeft: '0px' }}>
              <Col md={8}>
                <h1 className="mb-0">Historical Recovery</h1>
              </Col>
              <Col md={4} style={{ textAlign: 'right' }}>
                <Button color="secondary" className="mr-2" onClick={() => history.push('/market-screener')}>
                  Market Screener
                </Button>
                <Button color="warning" onClick={() => history.push(`/market-screener/edit/${id}`)}>
                  Edit Schedule
                </Button>
              </Col>
            </Row>
          </CardHeader>
          <CardBody>
            <Alert color="info">
              All months between start and end are included. The range is inclusive.
            </Alert>
            <Row className="mb-4">
              <Col md={4}>
                <strong>Name</strong>
                <div>{volumeRetrieve?.name || '-'}</div>
              </Col>
              <Col md={4}>
                <strong>Cliente</strong>
                <div>{volumeRetrieve?.cliente?.nome || '-'}</div>
              </Col>
              <Col md={4}>
                <strong>Locale</strong>
                <div>{volumeRetrieve?.locale || '-'}</div>
              </Col>
            </Row>
            <Row>
              <Col md={4}>
                <FormGroup>
                  <Label for="recoveryStartMonth" style={{ display: 'block' }}>
                    Recovery start month
                  </Label>
                  <Input
                    className="form-control-alternative"
                    type="date"
                    name="recoveryStartMonth"
                    id="recoveryStartMonth"
                    value={recoveryStartMonth}
                    onChange={(e) => setRecoveryStartMonth(e.target.value)}
                  />
                </FormGroup>
              </Col>
              <Col md={4}>
                <FormGroup>
                  <Label for="recoveryEndMonth" style={{ display: 'block' }}>
                    Recovery end month
                  </Label>
                  <Input
                    className="form-control-alternative"
                    type="date"
                    name="recoveryEndMonth"
                    id="recoveryEndMonth"
                    value={recoveryEndMonth}
                    onChange={(e) => setRecoveryEndMonth(e.target.value)}
                  />
                </FormGroup>
              </Col>
              <Col md={4}>
                <FormGroup>
                  <Label htmlFor="recoveryForce" style={{ display: 'block' }}>
                    Force historical overwrite
                  </Label>
                  <Input
                    type="checkbox"
                    id="recoveryForce"
                    checked={recoveryForce}
                    onChange={(e) => setRecoveryForce(e.target.checked)}
                    style={{ width: 'auto', marginLeft: '0px' }}
                  />
                </FormGroup>
              </Col>
            </Row>
            <Row style={{ display: 'flex', justifyContent: 'flex-end', marginRight: '0px' }}>
              <Button color="info" type="button" disabled={recoverySubmitting} onClick={openRecoveryConfirmation}>
                Recover month range
              </Button>
            </Row>
            <ToastContainer />
          </CardBody>
        </Card>
      </div>
    </>
  )
}

export default VolumeRetrieveHistoricalRecovery
